// ============================================================================
// PurchaseButton — CTA d'achat unifié cross-plateforme (iOS IAP / web Stripe).
// ============================================================================
// Ce composant remplace l'ancien `PaywallEmailCTA` sur iOS depuis la v1.0.9 :
// Apple Reader app exemption ne s'applique plus à Jeraisonne (edtech, hors
// catégorie autorisée). On DOIT proposer un In-App Purchase natif.
//
// Sur natif iOS/Android : ouvre la feuille système Apple/Google via RevenueCat.
// Sur web : redirige vers Stripe checkout (comportement existant).
// L'invalidation des queries après succès met immédiatement à jour l'UI.
//
// Le libellé du bouton et le prix affiché sont pilotés depuis `products.ts`
// (catalogue interne) — sur natif, RC surchargera le prix à l'achat si le
// store renvoie une valeur différente (localisation, offres…).
// ============================================================================

import { useState } from 'react';
import { cn } from '@jeraisonne/ui';
import { useQueryClient } from '@tanstack/react-query';
import { purchase, restorePurchases, getProductById } from '@/lib/checkout';
import type { CheckoutProductId } from '@/lib/checkout';
import { isNativeApp } from '@/lib/platform';

type Status = 'idle' | 'loading' | 'pending' | 'success' | 'error';

interface Props {
  productId: CheckoutProductId;
  /** Contexte libre pour analytics (chapter_type, source…) — pas d'usage
   *  fonctionnel côté serveur. */
  context?: Record<string, unknown>;
  className?: string;
  /** Affiche le bouton « Restaurer mes achats » sous le CTA d'achat.
   *  OBLIGATOIRE sur natif pour la review Apple. */
  showRestore?: boolean;
  /**
   * Appelé sur natif dès qu'un achat/restauration RÉUSSIT (feuille système
   * fermée, entitlement actif). Le webhook RC n'a pas encore forcément écrit
   * l'accès en base — au parent de sonder puis de reprendre le flux (ex. le
   * paywall d'exercice qui débloque et reprend l'exo). Si fourni, le parent
   * prend la main sur l'UX post-achat (la carte « Merci » interne peut ne
   * jamais s'afficher car le parent remplace ce bouton).
   */
  onPurchased?: () => void;
}

export function PurchaseButton({ productId, className, showRestore = true, onPurchased }: Props) {
  const product = getProductById(productId);
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | null>(null);
  const [restoring, setRestoring] = useState(false);

  function invalidateAccessCaches() {
    qc.invalidateQueries({ queryKey: ['student-subscription'] });
    qc.invalidateQueries({ queryKey: ['student-passes'] });
    qc.invalidateQueries({ queryKey: ['objective-pack-access'] });
  }

  async function handlePurchase() {
    setStatus('loading');
    setError(null);
    const result = await purchase(productId);
    if (result.status === 'success') {
      setStatus('success');
      // Le webhook RC/Stripe peut mettre 1-5s à écrire dans subscriptions.
      // On invalide tout de suite, puis re-invalide après 3s pour être safe.
      invalidateAccessCaches();
      setTimeout(invalidateAccessCaches, 3_000);
      onPurchased?.();
      return;
    }
    if (result.status === 'user_cancelled') {
      setStatus('idle');
      return;
    }
    if (result.status === 'pending') {
      setStatus('pending');
      return;
    }
    setStatus('error');
    setError(result.message);
  }

  async function handleRestore() {
    setRestoring(true);
    setError(null);
    const result = await restorePurchases();
    setRestoring(false);
    if (result.status === 'success') {
      invalidateAccessCaches();
      setStatus('success');
      onPurchased?.();
      return;
    }
    if (result.status === 'error') setError(result.message);
  }

  if (status === 'success') {
    return (
      <div className={cn('border-xp-200 bg-xp-50 rounded-xl border p-4 text-center', className)}>
        <p className="font-display text-ink-900 text-base font-extrabold">
          ✅ Merci pour ton soutien !
        </p>
        <p className="text-ink-600 mt-1 font-sans text-xs leading-relaxed">
          Ton accès est en cours d'activation… tu peux reprendre tes exercices.
        </p>
      </div>
    );
  }

  if (status === 'pending') {
    return (
      <div className={cn('border-ink-200 bg-ink-50 rounded-xl border p-4 text-center', className)}>
        <p className="font-display text-ink-900 text-sm font-bold">Achat en attente</p>
        <p className="text-ink-500 mt-1 font-sans text-xs leading-relaxed">
          Un parent doit approuver ton achat (Ask to Buy). Tu recevras une notification.
        </p>
      </div>
    );
  }

  return (
    <div className={className}>
      <button
        onClick={handlePurchase}
        disabled={status === 'loading'}
        className="bg-primary-400 font-display hover:bg-primary-500 min-h-11 w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
      >
        {status === 'loading'
          ? isNativeApp()
            ? 'Ouverture…'
            : 'Redirection…'
          : `Souscrire · ${product.priceLabel}`}
      </button>

      {showRestore && isNativeApp() && (
        <button
          onClick={handleRestore}
          disabled={restoring}
          className="text-ink-500 hover:text-ink-700 mt-2 w-full py-2 font-sans text-xs font-semibold transition-all active:scale-[.98] disabled:opacity-60"
        >
          {restoring ? 'Restauration…' : 'Restaurer mes achats'}
        </button>
      )}

      {status === 'error' && error && (
        <p className="text-danger-500 mt-2 text-center font-sans text-xs">
          Une erreur est survenue ({error}). Réessaie dans un instant.
        </p>
      )}
    </div>
  );
}
